import React, { useEffect, useRef, useState } from 'react';
import type { MenuLanguage } from './types.js';

export function MatchmakingStatus({
  language,
  confirmed = true,
  position,
  playersWaiting,
  estimatedWait,
  title,
  onCancel,
}: {
  language: MenuLanguage;
  confirmed?: boolean;
  position: number;
  playersWaiting: number;
  estimatedWait: number;
  title?: string;
  onCancel?: () => void;
}) {
  const [remainingWait, setRemainingWait] = useState(estimatedWait);
  const spinnerRef = useRef<HTMLDivElement>(null);
  const tr = (spanish: string, english: string, french: string) =>
    language === 'es' ? spanish : language === 'fr' ? french : english;

  useEffect(() => {
    if (!confirmed || estimatedWait <= 0) {
      setRemainingWait(estimatedWait);
      return;
    }

    const deadline = Date.now() + estimatedWait;
    const refresh = () => setRemainingWait(Math.max(0, deadline - Date.now()));
    refresh();
    const timer = window.setInterval(refresh, 200);
    return () => window.clearInterval(timer);
  }, [confirmed, estimatedWait]);

  useEffect(() => {
    let frame = 0;
    const animate = (now: number) => {
      if (spinnerRef.current) {
        const angle = (now % 850) / 850 * 360;
        spinnerRef.current.style.transform = `rotate(${angle}deg)`;
      }
      frame = window.requestAnimationFrame(animate);
    };
    frame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <section className="queue-card" aria-live="polite">
      <div ref={spinnerRef} className="queue-spinner" />
      <strong>{title || tr('Buscando partida...', 'Finding a match...', "Recherche d'une partie...")}</strong>
      {confirmed && (
        <>
          <span>{tr('Posición en cola', 'Queue position', 'Position dans la file')}: {position}</span>
          <span>
            {tr('Jugadores esperando', 'Players waiting', 'Joueurs en attente')}: {playersWaiting} / 32
          </span>
          {remainingWait > 0 && (
            <span>
              {tr('La partida empieza en', 'Match starts in', 'La partie commence dans')}: {Math.ceil(remainingWait / 1000)} s
            </span>
          )}
        </>
      )}
      {onCancel && (
        <button onClick={onCancel}>
          {tr('Cancelar búsqueda', 'Cancel search', 'Annuler la recherche')}
        </button>
      )}
    </section>
  );
}
